// PremiumServiceDetails.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getServicesList } from "services/serviceslist/services";
import { FaArrowLeft, FaStethoscope, FaPhoneAlt } from "react-icons/fa";
import "./ServiceDetails.scss";

const UltraPremiumServiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  const slugify = (text) =>
    text.toString().toLowerCase().trim().replace(/\s+/g, "-").replace(/[^\w-]+/g, "").replace(/--+/g, "-");

  useEffect(() => {
    const fetchService = async () => {
      try {
        const res = await getServicesList({ page: 1, limit: 50 });
        if (res?.code === 200) {
          const found = res.data.services.find((s) => slugify(s.title) === id);
          setService(found || null);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [id]);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="premium-loader"></div>
        <p>Loading your care solution...</p>
      </div>
    );
  }

  if (!service) {
    return (
      <div className="not-found">
        <h2>Service Not Found</h2>
        <button onClick={() => navigate("/services")} className="back-btn">
          ← Back to Services
        </button>
      </div>
    );
  }

  return (
    <>
      {/* HERO SECTION */}
      <section className="service-hero">
        <div className="hero-content">
          <h1>{service.title}</h1>
          <p className="hero-subtitle">{service.shortdesc}</p>
          <div className="trust-badges">
            <span>Verified Nurses</span>
            <span>24×7 Support</span>
            <span>50,000+ Families Served</span>
          </div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <section className="service-main">
        <div className="service-container">
          {/* LEFT SIDE – IMAGE + CTA */}
          <div className="service-image-sticky">
            <img src={service.imageUrl} alt={service.title} className="main-img" />

            <div className="sticky-cta">
              <h3>Need This Service?</h3>
              <p>Our care coordinator will call you in <strong>5 minutes</strong></p>

              <a
                href="tel:+918291008181"
                className="call-now-btn"
                style={{
                  display: 'inline-flex', // inline-flex ensures it stays inline with proper flex behavior
                  alignItems: 'center', // vertical center alignment
                  justifyContent: 'center',
                  gap: '10px',
                  padding: '18px 28px',
                  background: 'linear-gradient(135deg, #1e3a8a, #1e40af)',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '16px',
                  textDecoration: 'none',
                  borderRadius: '16px',
                  border: '2px solid #3b82f6',
                  boxShadow: '0 6px 20px rgba(30, 64, 175, 0.25)',
                  transition: 'all 0.35s ease',
                  minWidth: '200px',
                  maxWidth: '100%',
                  fontFamily: '"Poppins", sans-serif',
                  textAlign: 'center',
                  lineHeight: '1', // important: prevents icon from moving
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #1e40af, #2563eb)';
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 14px 32px rgba(30, 64, 175, 0.4)';
                  e.currentTarget.style.borderColor = '#60a5fa';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #1e3a8a, #1e40af)';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(30, 64, 175, 0.25)';
                  e.currentTarget.style.borderColor = '#3b82f6';
                }}
              >
                <FaPhoneAlt style={{ fontSize: '18px', verticalAlign: 'middle' }} /> Call Now: +91 82910 08181
              </a>


              <a
                href="https://wa.me/918291008181"
                target="_blank"
                rel="noreferrer"
                className="whatsapp-btn"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '12px',
                  padding: '17px 32px',
                  background: '#25d366',
                  color: 'white',
                  fontWeight: '600',
                  fontSize: '16px',
                  textDecoration: 'none',
                  borderRadius: '16px',
                  border: '2px solid #25d366',
                  boxShadow: '0 6px 20px rgba(37, 211, 102, 0.3)',
                  transition: 'all 0.35s ease',
                  minWidth: '280px',
                  marginTop: '16px',
                  fontFamily: '"Poppins", sans-serif'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#128c7e';
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 14px 32px rgba(18, 140, 126, 0.45)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#25d366';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(37, 211, 102, 0.3)';
                }}
              >
                <FaStethoscope style={{ fontSize: '20px' }} /> Chat on WhatsApp
              </a>
            </div>
          </div>

          {/* RIGHT SIDE – DESCRIPTION */}
          <div className="service-content">
            <div className="section-title">
              <h2>About This Service</h2>
              <div className="title-underline"></div>
            </div>

            <div className="premium-card">
              <p className="short-desc">{service.shortdesc}</p>
              <p
                className="long-desc"
                dangerouslySetInnerHTML={{ __html: service.longdesc }}
              ></p>

            </div>


            {/* Bottom CTA */}
            <div className="bottom-cta">
              <button onClick={() => navigate("/contactus")} className="enquire-btn">
                Book Free Consultation
              </button>
              <button onClick={() => navigate("/services")} className="back-btn">
                <FaArrowLeft /> View All Services
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default UltraPremiumServiceDetails;
